import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { PrayerTime, PrayerTimeSection } from "@/types/page";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { usePageContext } from "@/hooks/usePageContext";

interface PrayerTimesPageProps {
  content: any;
}

const PrayerTimesPage: React.FC<PrayerTimesPageProps> = ({
  content
}) => {
  // Create a state for animation key to force remounting
  const [animationKey, setAnimationKey] = useState(Date.now());
  const { currentUser } = usePageContext();
  
  // Reset animation when component mounts or content changes
  useEffect(() => {
    setAnimationKey(Date.now());
    console.log("PrayerTimesPage content received:", content);
  }, [content]);

  // Get animation speed from content or use default
  const animationSpeed = content?.animationSpeed || 20;

  // Helper function to check if a prayer time value exists
  const hasValue = (value: string | undefined): boolean => {
    return !!value && value.trim() !== '';
  };

  // Handle both the section format, the array format, and the legacy format
  let sections: PrayerTimeSection[] = [];
  
  // Check if content has a sections array with prayer times
  if (content.sections && Array.isArray(content.sections) && content.sections.length > 0) {
    // Use provided sections directly
    sections = content.sections.map(section => ({
      ...section,
      // Ensure title is present
      title: section.title || content.blockTitle || "זמני תפילה"
    }));
    console.log("Using sections format with sections:", sections);
  }
  // If no sections but has prayerTimes array, create a single section
  else if (content.prayerTimes && Array.isArray(content.prayerTimes) && content.prayerTimes.length > 0) {
    const sectionTitle = content.blockTitle || content.regularSectionTitle || "זמני תפילה";
    console.log("Using prayerTimes array format with title:", sectionTitle);
    sections.push({
      id: "prayer-times",
      title: sectionTitle,
      prayerTimes: content.prayerTimes
    });
  } 
  // Legacy format with individual prayer time properties
  else {
    const prayerTimes: PrayerTime[] = [];
    const sectionTitle = content.blockTitle || content.regularSectionTitle || "זמני תפילה";
    console.log("Using legacy format with title:", sectionTitle);
    
    // Check for individual prayer time fields
    if (content.shacharit && hasValue(content.shacharit)) {
      prayerTimes.push({
        id: "1",
        title: "שחרית",
        time: content.shacharit
      });
    }
    if (content.mincha && hasValue(content.mincha)) {
      prayerTimes.push({
        id: "2",
        title: "מנחה",
        time: content.mincha
      });
    }
    if (content.maariv && hasValue(content.maariv)) {
      prayerTimes.push({
        id: "3",
        title: "ערבית",
        time: content.maariv
      });
    }
    if (content.shabbatIn && hasValue(content.shabbatIn)) {
      prayerTimes.push({
        id: "4",
        title: "כניסת שבת",
        time: content.shabbatIn,
        isShabbat: true
      });
    }
    if (content.shabbatOut && hasValue(content.shabbatOut)) {
      prayerTimes.push({
        id: "5",
        title: "יציאת שבת",
        time: content.shabbatOut,
        isShabbat: true
      });
    }

    // If we found any prayer times, create a section
    if (prayerTimes.length > 0) {
      sections.push({
        id: "prayer-times",
        title: sectionTitle,
        prayerTimes: prayerTimes
      });
    }
  }

  // Filter sections to only include those with valid prayer times
  const validSections = sections
    .filter(section => section.prayerTimes && section.prayerTimes.length > 0)
    .map(section => ({
      ...section,
      // Filter to only include prayer times with valid time and title
      prayerTimes: section.prayerTimes.filter(prayer => 
        hasValue(prayer.time) && (hasValue(prayer.title) || hasValue(prayer.customTitle))
      )
    }))
    .filter(section => section.prayerTimes.length > 0);

  if (validSections.length === 0) {
    return (
      <Alert className="mb-3 sm:mb-4 md:mb-6 bg-white/20 backdrop-blur-md text-black p-3 sm:p-4 md:p-6">
        <AlertTitle className="text-lg sm:text-xl md:text-2xl">אין זמני תפילה להצגה</AlertTitle>
        <AlertDescription>
          <p className="text-base sm:text-lg md:text-xl text-black/90">
            לא הוגדרו זמני תפילה עבור דף זה.
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  // Flatten all prayer times into a single array
  const allPrayerTimes = validSections.reduce((acc, section) => [...acc, ...section.prayerTimes], [] as PrayerTime[]);
  const totalPrayerTimes = allPrayerTimes.length;
  const shouldAnimate = totalPrayerTimes > 4;

  // Calculate animation duration including the 5-second pause
  const totalDuration = animationSpeed + 5;

  // CSS for the custom marquee animation
  const customMarqueeStyle = shouldAnimate 
    ? {
        animationName: 'customMarquee',
        animationDuration: `${totalDuration}s`,
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
      } 
    : {};

  return (
    <div className="flex flex-col min-h-screen">
      {/* Define custom keyframes animation */}
      {shouldAnimate && (
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes customMarquee {
              0% { transform: translateY(0); }
              20% { transform: translateY(0); } /* 5 seconds pause (5/${totalDuration} = ~20%) */
              100% { transform: translateY(-${totalPrayerTimes > 4 ? 100 : 0}%); }
            }
          `
        }} />
      )}
      
      <div className="flex-1 w-1/2">
        {validSections.map(section => (
          <div key={section.id} className="mb-4 sm:mb-6 md:mb-10">
            <div className="rounded-2xl p-3 sm:p-4 md:p-6 bg-white/20 backdrop-blur-md shadow-2xl h-full space-y-4 md:space-y-6">
              {/* Section Title */}
              <h3 className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-center text-black mb-3 sm:mb-4 md:mb-6">
                {section.title || "זמני תפילה"}
              </h3>
              
              {/* Prayer Times Display */}
              <div className="h-[70vh] overflow-hidden relative">
                <div 
                  key={animationKey} 
                  className="absolute top-0 left-0 right-0 flex flex-col gap-6 items-center"
                  style={customMarqueeStyle}
                >
                  {allPrayerTimes.map((prayer, index) => (
                    <div key={prayer.id} className="w-full">
                      <Card className="bg-white/20 backdrop-blur-md shadow-2xl h-full transition-all duration-300 rounded-xl overflow-hidden border border-black/20">
                        <CardContent className="p-3 sm:p-4 md:p-6 h-full">
                          <div className="flex flex-col justify-center items-center text-center space-y-2 sm:space-y-3 md:space-y-4 h-full">
                            <h3 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold text-black tracking-wide">
                              {prayer.title === 'custom' ? prayer.customTitle : prayer.title}
                            </h3>
                            <p className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black/90">
                              {prayer.time}
                            </p>
                            {prayer.isShabbat && (
                              <Badge variant="outline" className="bg-black/10 border-black/30 text-black text-lg sm:text-xl font-bold">
                                שבת
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrayerTimesPage;
